// components/InventaireModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";

function TypeBadge({ value }) {
  const v = (value || "").toLowerCase();
  const isMobilier = v === "mobilier";
  const classes = isMobilier
    ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
    : "bg-indigo-50 text-indigo-700 ring-indigo-600/20";
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${classes}`}>
      {value || "—"}
    </span>
  );
}

function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toCsv(rows) {
  const headers = ["Nom", "Type", "Valeur"];
  const lines = [
    headers.join(";"),
    ...rows.map((r) =>
      [r.nom, r.type, r.valeur]
        .map((cell) => {
          const s = String(cell ?? "");
          if (s.includes(";") || s.includes("\n") || s.includes('"')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(";")
    ),
  ];
  return lines.join("\n");
}

function xlsFromRows(rows) {
  const safe = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const tableRows = rows
    .map(
      (r) => `
      <tr>
        <td>${safe(r.nom)}</td>
        <td>${safe(r.type)}</td>
        <td>${safe(r.valeur)}</td>
      </tr>`
    )
    .join("");

  return `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Inventaire</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <meta charset="UTF-8" />
      </head>
      <body>
        <table border="1">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Valeur</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `.trim();
}

// 🧩 extrait les lignes (items_json) depuis la réponse Supabase
function extractRowsFromSupabase(payload) {
  // Le back peut renvoyer directement un tableau de records, ou { ok, records: [...] }
  const records = Array.isArray(payload?.records)
    ? payload.records
    : Array.isArray(payload)
    ? payload
    : [];

  if (records.length === 0) return [];

  // On prend l’enregistrement le plus récent
  const latest = [...records].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )[0];

  const items = Array.isArray(latest.items_json) ? latest.items_json : [];

  return items.map((it, idx) => ({
    id: it.uid || `${latest.id}-${idx}`,
    nom: it.nom ?? "",
    type: it.type ?? "",
    valeur: it.valeur ?? "",
  }));
}

// https://cloterenotaire.onrender.com
// http://localhost:5050
export default function InventaireModal({
  open,
  onClose,
  id_action,
  apiBase = API_BASE,
  title = "Inventaire",
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const filenameSuffix = useMemo(() => (id_action ? `_${id_action}` : ""), [id_action]);

  useEffect(() => {
    if (!open || !id_action) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`${apiBase}/api/getInventaire`, { params: { id_action } });
        if (cancelled) return;

        // ⚠️ Le back renvoie un tableau de lignes inventaire (records Supabase)
        // avec items_json à l’intérieur. On mappe vers `rows`.
        const nextRows = extractRowsFromSupabase(res.data);
        setRows(nextRows);
      } catch (e) {
        console.error("❌ fetch inventaire:", e);
        if (!cancelled) setError("Impossible de charger l’inventaire.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, id_action, apiBase]);

  const handleExportCSV = () => {
    const csv = toCsv(rows);
    downloadBlob(csv, "text/csv;charset=utf-8", `inventaire${filenameSuffix}.csv`);
  };

  const handleExportXLS = () => {
    const html = xlsFromRows(rows);
    downloadBlob(html, "application/vnd.ms-excel;charset=utf-8", `inventaire${filenameSuffix}.xls`);
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
      <div className="fixed inset-0 p-4 flex items-center justify-center">
        {/* Panel quasi plein écran */}
        <DialogPanel className="pointer-events-auto w-full max-w-7xl h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold text-gray-900">{title}</DialogTitle>
              
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                className="btn-main"
                disabled={loading || rows.length === 0}
              >
                Exporter CSV
              </button>
              <button
                onClick={onClose}
                className="rounded-md p-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
                title="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="rounded-md bg-slate-50 p-4 mb-3">
                <p className="text-sm text-slate-700 m-0">Chargement de l’inventaire…</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-3">
                <p className="text-sm text-red-700 m-0">{error}</p>
              </div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div className="rounded-md bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 m-0">Aucun élément dans cet inventaire.</p>
              </div>
            )}

            {!loading && !error && rows.length > 0 && (
              <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Nom
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Valeur
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {rows.map((item) => (
                      <tr key={item.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {item.nom}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <TypeBadge value={item.type} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {item.valeur} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}